import { Button, Chip, Link, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from "@heroui/react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import FileUpload from "../ui/FileUpload/FileUpload";
import { ErrorMessage } from "../ui/ErrorMessage/ErrorMessage";
import { useAppApi } from "src/appApi";

/** A new upload is only mandatory when the request has no doctor document yet.
 *  One generated from a template already satisfies the requirement — asking for
 *  it again would mean attaching the same recommendation twice. */
const makeSchema = (hasExistingDocuments: boolean) =>
	z.object({
		files: z
			.array(z.object({ file: z.instanceof(File).optional() }))
			.refine(
				(files) => hasExistingDocuments || !!files.find((f) => !!f.file),
				"לפחות קובץ אחד נדרש",
			),
		approveMessageByDoctor: z.string().optional(),
	});

type TForm = z.infer<ReturnType<typeof makeSchema>>;

export function DoctorApproveRequestModal({
	existingDocuments = [],
	onSubmit,
}: {
	/** Documents already attached to the request — typically a recommendation the
	 *  doctor generated from a template. Shown so they can approve with it
	 *  instead of hunting for the file and uploading it a second time. */
	existingDocuments?: NonNullable<TRequest["doctorDocuments"]>;
	onSubmit: (data: { files: File[]; approveMessageByDoctor?: string }) => void | Promise<void>;
}) {
	const appApi = useAppApi();
	const hasExistingDocuments = existingDocuments.length > 0;
	const { handleSubmit, control, getFieldState, formState } = useForm<TForm>({
		resolver: zodResolver(makeSchema(hasExistingDocuments)),
		defaultValues: {
			files: [],
			approveMessageByDoctor: "",
		},
	});

	// const { fields } = useFieldArray({ control, name: "files" });

	// One approval per dialog. Each submission uploads the files again and
	// appends them, so a second one — a double click — attaches every file twice.
	const submitted = useRef(false);
	const _onSubmit = handleSubmit(
		async (data) => {
			if (submitted.current) return;
			submitted.current = true;
			const r = data.files.filter((f) => !!f.file && f.file instanceof File);
			const files: File[] = r.map((f) => f.file as File);

			try {
				await onSubmit?.({
					files,
					approveMessageByDoctor: data.approveMessageByDoctor || undefined,
				});
			} catch (error) {
				submitted.current = false; // nothing was saved — allow a retry
				throw error;
			}
		},
		(error) => {
			console.error(error);
			appApi.api.system.uiLogs({
				message: "DoctorApproveRequestModal form validation error",
				severity: "ERROR",
				errors: error,
			});
		}
	);

	const ref = useRef<HTMLFormElement>(null);

	return (
		<ModalContent>
			{(onClose) => (
				<>
					<ModalHeader>אישור בקשה</ModalHeader>
					<ModalBody className="py-0">
						<form onSubmit={_onSubmit} ref={ref}>
							{hasExistingDocuments && (
								<div className="mb-4 rounded-medium border border-default-200 bg-default-50 p-3">
									<p className="mb-2 text-small font-medium text-default-700">
										מסמכים שכבר צורפו לבקשה
									</p>
									<div className="flex flex-col gap-2">
										{existingDocuments.map((document) => (
											<div
												key={document.path || document.url}
												className="flex items-center justify-between gap-2"
											>
												<Link
													href={document.url}
													isExternal
													showAnchorIcon
													size="sm"
													className="truncate"
												>
													{document.name || "מסמך"}
												</Link>
												<Chip
													size="sm"
													variant="flat"
													color="success"
													startContent={<Icon icon="lucide:check" className="h-3 w-3" />}
												>
													צורף
												</Chip>
											</div>
										))}
									</div>
									<p className="mt-2 text-tiny text-default-500">
										אפשר לאשר כך, או להוסיף מסמכים נוספים למטה
									</p>
								</div>
							)}
							{[1, 2, 3].map((field, index) => {
								return (
									<Controller
										key={field}
										control={control}
										name={`files.${index}.file`}
										render={({ field }) => {
											const fieldState = getFieldState(field.name);

											return (
												<FileUpload
													file={field.value}
													onChange={(file) => {
														field.onChange(file);
													}}
													errorMessage={fieldState.error?.message}
													isInvalid={!!fieldState.error}
												/>
											);
										}}
									/>
								);
							})}

							<Controller
								name="approveMessageByDoctor"
								control={control}
								render={({ field }) => (
									<Textarea
										{...field}
										label="הערת אישור (אופציונלי)"
										placeholder="הוסף הערה או הנחיות נוספות (אופציונלי)"
										description="הערה זו תוצג למטופל ולאדמין"
										maxLength={500}
										className="mt-4"
									/>
								)}
							/>

							<ErrorMessage>
								{(formState.errors.root?.message ||
									formState.errors.files?.root?.message) ??
									""}
							</ErrorMessage>

							<ModalFooter className="px-0">
								<Button variant="light" onPress={onClose}>
									ביטול
								</Button>
								{/* Submits through the form only: a type="submit" button that also
								    called requestSubmit() on press submitted twice per click. */}
								<Button type="submit" color="primary" isLoading={formState.isSubmitting}>
									אישור
								</Button>
							</ModalFooter>
						</form>
					</ModalBody>
				</>
			)}
		</ModalContent>
	);
}
